import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { allCharts, allTables, filledCells, readWorkbook } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

/** Diagrammarten, die räumlich verzerren – 3D, Kegel, Pyramide, Zylinder. */
function istRaeumlich(chartType: string): boolean {
  return /3D|Cone|Pyramid|Cylinder/i.test(chartType);
}

export const lektionTypWaehlen: Lesson = {
  id: "typ-waehlen",
  title: "Den richtigen Diagrammtyp wählen",
  summary: "Der Diagrammtyp ist eine Aussage über die Daten – nicht über den Geschmack.",
  durationMinutes: 11,
  learningGoals: [
    "Den Diagrammtyp aus der Frage ableiten, die beantwortet werden soll",
    "Ein Diagramm aus einer Tabelle erzeugen",
    "Räumliche Diagramme als Verzerrung erkennen",
  ],
  steps: [
    {
      id: "welcher-typ",
      title: "Erst die Frage, dann der Typ",
      explanation: [
        "Ein Diagramm beantwortet eine Frage. Welche das ist, entscheidet über den Typ " +
          "– und diese Reihenfolge wird meist umgedreht, weshalb so viele Diagramme " +
          "hübsch aussehen und nichts sagen.",
        "Geht es um einen Vergleich zwischen Kategorien – welche Region hat mehr " +
          "verkauft? –, ist das Säulen- oder Balkendiagramm richtig. Balken " +
          "waagerecht sind dabei die bessere Wahl, sobald die Beschriftungen lang " +
          "sind: Sie stehen dann lesbar nebeneinander statt schräg gedreht.",
        "Geht es um eine Entwicklung über die Zeit, gehört das Liniendiagramm hin. Die " +
          "Linie behauptet Kontinuität zwischen den Punkten, und genau das ist bei " +
          "einem Zeitverlauf gemeint. Für Kategorien ohne natürliche Reihenfolge wäre " +
          "sie eine Falschaussage – zwischen „Nord“ und „Süd“ gibt es nichts.",
        "Geht es um Anteile an einem Ganzen, kommt das Kreisdiagramm in Frage – aber " +
          "nur bei zwei bis vier Segmenten. Der Mensch kann Winkel schlecht " +
          "vergleichen, deutlich schlechter als Längen. Ab fünf Segmenten ist ein " +
          "sortiertes Balkendiagramm in jeder Hinsicht überlegen, und bei zwölf " +
          "Segmenten ist der Kreis nur noch Dekoration.",
        "Und für den Zusammenhang zweier Größen – hängt der Umsatz an der Zahl der " +
          "Besuche? – gibt es das Punktdiagramm. Es ist das einzige, das zwei " +
          "Zahlenachsen hat; bei allen anderen ist die waagerechte Achse eine " +
          "Aufzählung.",
      ],
      keyPoints: [
        "Vergleich → Säule oder Balken. Zeitverlauf → Linie.",
        "Anteile → Kreis, aber nur bei bis zu vier Segmenten.",
        "Zusammenhang zweier Zahlen → Punktdiagramm (XY).",
        "Lange Beschriftungen sprechen für Balken statt Säulen.",
      ],
    },
    {
      id: "diagramm-anlegen",
      title: "Ein Diagramm aus einer Tabelle",
      explanation: [
        "Cursor in die Daten setzen, dann „Einfügen“ → gewünschter Diagrammtyp. Excel " +
          "erkennt den Bereich selbst und benutzt die Kopfzeile als Beschriftung – ein " +
          "weiterer Grund, warum die Listenform aus Modul 1 wichtig ist.",
        "Der entscheidende Handgriff kommt davor: Erzeuge das Diagramm aus einer " +
          "Tabelle im Sinne von Modul 5, nicht aus einem losen Bereich. Ein Diagramm " +
          "über einer Tabelle wächst automatisch mit, wenn unten Zeilen dazukommen. " +
          "Ein Diagramm über B2:D40 zeigt auch dann noch die alten vierzig Zeilen, " +
          "wenn die Daten längst bis Zeile 300 reichen.",
        "Mit Alt + F1 entsteht ein Diagramm direkt auf dem aktuellen Blatt, mit F11 " +
          "eines auf einem eigenen Diagrammblatt. Ein eigenes Blatt lohnt sich für " +
          "Diagramme, die gedruckt oder präsentiert werden; für die Arbeit neben den " +
          "Daten ist das eingebettete Diagramm praktischer.",
        "Stimmt die Zuordnung von Zeilen und Spalten nicht – stehen also die Regionen " +
          "auf der Achse, wo die Monate hingehören –, hilft unter „Diagrammentwurf“ " +
          "die Schaltfläche „Zeile/Spalte wechseln“. Das ist ein Klick und erspart, " +
          "das Diagramm neu aufzubauen.",
      ],
      keyPoints: [
        "Alt + F1 erzeugt ein Diagramm auf dem Blatt, F11 eines auf eigenem Blatt.",
        "Aus einer Tabelle erzeugen – dann wächst das Diagramm mit.",
        "Diagrammentwurf → Zeile/Spalte wechseln dreht die Zuordnung um.",
      ],
      task: {
        instruction: "Erzeuge aus deinen Daten ein Diagramm.",
        hint: "Cursor in die Tabelle setzen, dann Einfügen → Diagrammtyp – oder Alt + F1 für einen schnellen Vorschlag.",
        check: async () => {
          const mappe = await readWorkbook();
          const diagramme = allCharts(mappe);

          if (diagramme.length === 0) {
            if (mappe.features.cells && filledCells(mappe).length === 0) {
              return fail(KEIN_INHALT, "Trage zuerst deine Liste ein.");
            }
            return fail(
              "In der Arbeitsmappe gibt es noch kein Diagramm.",
              "Setze den Cursor in deine Daten und drücke Alt + F1.",
            );
          }

          const erstes = diagramme[0];
          const tabellen = allTables(mappe);

          // Auf welchen Bereich sich ein Diagramm stützt, gibt Office.js nicht
          // her - es gibt keine lesbare Eigenschaft dafür. Der Hinweis auf die
          // Tabelle bleibt deshalb ein Hinweis und wird als solcher benannt.
          const hinweis =
            tabellen.length === 0
              ? " Ein Hinweis dazu: Auf dem Blatt gibt es keine Tabelle. Ob das Diagramm auf einem " +
                "festen Bereich sitzt, lässt sich nicht auslesen – prüfe es selbst über " +
                "„Diagrammentwurf“ → „Daten auswählen“."
              : "";

          return pass(
            `Diagramm „${erstes.name}“ auf Blatt „${erstes.sheetName}“` +
              (erstes.chartType ? ` vom Typ „${erstes.chartType}“` : "") +
              (erstes.seriesCount !== null ? ` mit ${erstes.seriesCount} Datenreihe${erstes.seriesCount === 1 ? "" : "n"}` : "") +
              "." +
              hinweis,
          );
        },
      },
    },
    {
      id: "kein-3d",
      title: "Keine räumlichen Diagramme",
      explanation: [
        "Excel bietet zu fast jedem Diagrammtyp eine räumliche Variante an: 3D-Säulen, " +
          "3D-Kreise, dazu Kegel, Pyramiden und Zylinder. Keine davon fügt " +
          "Information hinzu. Alle nehmen welche weg.",
        "Der Grund ist die Perspektive. In einem 3D-Säulendiagramm sind die vorderen " +
          "Säulen größer abgebildet als die hinteren, obwohl ihr Wert kleiner sein " +
          "kann. Die Grundfläche verdeckt einen Teil der Säulen. Und die Höhe lässt " +
          "sich an der schräg stehenden Achse nicht mehr ablesen. Beim 3D-Kreis kommt " +
          "hinzu, dass das vordere Segment durch die Kippung deutlich größer wirkt – " +
          "ein Effekt, mit dem sich Zahlen wunderbar schönen lassen.",
        "Kegel, Pyramide und Zylinder haben dasselbe Problem in verschärfter Form: " +
          "Bei einem Kegel läuft die Spitze zusammen, sodass die oberen zwanzig " +
          "Prozent der Höhe kaum noch Fläche haben. Zwei Werte, die sich um zehn " +
          "Prozent unterscheiden, sehen dadurch fast gleich aus.",
        "Die Regel ist deshalb schlicht: Zweidimensional bleiben. Wer eine dritte " +
          "Größe darstellen will, nimmt ein Blasendiagramm oder mehrere kleine " +
          "Diagramme nebeneinander – beides liest sich besser als jede räumliche " +
          "Darstellung.",
      ],
      keyPoints: [
        "3D-Säulen verdecken sich gegenseitig und verzerren durch die Perspektive.",
        "Beim 3D-Kreis wirkt das vordere Segment größer, als es ist.",
        "Kegel und Pyramide verzerren am stärksten – die Spitze hat kaum Fläche.",
      ],
      task: {
        instruction:
          "Prüfe deine Diagramme: Keines soll eine räumliche Darstellung verwenden – " +
          "also kein 3D, kein Kegel, keine Pyramide, kein Zylinder.",
        hint: "Diagramm anklicken, dann Diagrammentwurf → Diagrammtyp ändern und die flache Variante wählen.",
        check: async () => {
          const mappe = await readWorkbook();
          const diagramme = allCharts(mappe);

          if (diagramme.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch kein Diagramm.",
              "Löse zuerst die Aufgabe im vorigen Schritt.",
            );
          }

          if (!mappe.features.chartType) {
            return nichtPruefbar(
              "Die Art eines Diagramms",
              "1.7",
              "Klicke dein Diagramm an und sieh unter „Diagrammentwurf“ → „Diagrammtyp ändern“ nach, " +
                "welcher Typ dort markiert ist.",
            );
          }

          const raeumlich = diagramme.filter(
            (diagramm) => diagramm.chartType !== null && istRaeumlich(diagramm.chartType),
          );

          if (raeumlich.length > 0) {
            const erstes = raeumlich[0];
            return fail(
              `Das Diagramm „${erstes.name}“ auf „${erstes.sheetName}“ ist vom Typ ` +
                `„${erstes.chartType}“ – eine räumliche Darstellung.`,
              "Klicke das Diagramm an und wähle unter „Diagrammentwurf“ → „Diagrammtyp ändern“ die " +
                "flache Entsprechung. Die Daten bleiben dabei unverändert.",
            );
          }

          return pass(
            `Alle ${diagramme.length} Diagramme sind flach dargestellt` +
              `${diagramme[0].chartType ? ` (${[...new Set(diagramme.map((d) => d.chartType))].join(", ")})` : ""} – ` +
              "die Höhen und Längen lassen sich damit ehrlich vergleichen.",
          );
        },
      },
    },
  ],
};
